import { DownloadOutlined } from '@ant-design/icons';
import { Formik } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as permissionKind from '../../../../shared/permissions';
import { type EventDataFragment } from '../../../api/fragments/EventData';
import { ApplicationStage, ContentRefinementSourceAction, ContentRefinementSourceKind } from '../../../api/types';
import { useAccountContext } from '../../../components/contexts/AccountContextManager';
import { useMultipleDealerIds } from '../../../components/contexts/DealerContextManager';
import { GenericStateOnPageContextProvider } from '../../../components/contexts/GenericStateOnPageContextManager';
import {
    type ExportModalInput,
    useExportFormatModal,
} from '../../../components/fields/DownloadModal/ExportFormatModal';
import { usePasswordModal } from '../../../components/fields/DownloadModal/PasswordModal';
import Form from '../../../components/fields/Form';
import BasicProPageWithHeader from '../../../layouts/BasicProLayout/BasicProPageWithHeader';
import { useThemeComponents } from '../../../themes';
import breakpoints from '../../../utilities/breakpoints';
import { ExportFormat, exportEventsApplication } from '../../../utilities/export';
import hasPermissions from '../../../utilities/hasPermissions';
import EventDetailsTabs from '../../admin/EventDetailsPage/EventDetailsTabs';
import EventDetailTabPanes from '../../admin/EventDetailsPage/EventDetailTabPanes';
import { useEventFormValidator } from '../../admin/EventDetailsPage/EventForm';
import { useEventInitialValues, useEventUpdateSubmission } from '../../admin/EventDetailsPage/utils';

export const StyledBasicProPageWithHeader = styled(BasicProPageWithHeader)`
    & .ant-pro-page-container-children-content {
        margin-left: 24px;
        margin-right: 24px;

        @media screen and (min-width: ${breakpoints.md}) {
            margin-left: 60px;
            margin-right: 60px;
        }
    }
`;

export enum Tab {
    MainDetails = 'MainDetails',
    CustomerInformation = 'CustomerInformation',
    Reservations = 'Reservations',
    Leads = 'Leads',
    Appointments = 'Appointments',
}

export type EventDetailsInnerPageProps = {
    event: EventDataFragment;
    disabled?: boolean;
};

const EventDetailsInnerPage = ({ event, disabled = false }: EventDetailsInnerPageProps) => {
    const { t } = useTranslation(['eventDetails']);
    const { Button } = useThemeComponents();

    const [currentTab, setCurrentTab] = useState<Tab>(Tab.MainDetails);
    const [loadedAppointmentIds, setLoadedAppointmentIds] = useState<string[]>([]);
    const [selectedAppointments, setSelectedAppointments] = useState<string[]>([]);
    const [loadedReservationIds, setLoadedReservationIds] = useState<string[]>([]);
    const [loadedLeadIds, setLoadedLeadIds] = useState<string[]>([]);
    const [selectedReservations, setSelectedReservations] = useState<string[]>([]);
    const [selectedLeads, setSelectedLeads] = useState<string[]>([]);

    const passwordModal = usePasswordModal();
    const exportFormatModal = useExportFormatModal({ format: ExportFormat.CapFormat });

    const { id: eventId, identifier } = event;

    const { token } = useAccountContext();

    const refineContentParams = useMemo(
        () => ({
            hasPermissionToRefineContent: hasPermissions(event.permissions, [permissionKind.updateEvent]),
            source: {
                kind: ContentRefinementSourceKind.Event,
                action: ContentRefinementSourceAction.Update,
                identifier: eventId,
            },
        }),
        [event.permissions, eventId]
    );

    const navigate = useNavigate();

    const goBack = useCallback(() => {
        navigate('..');
    }, [navigate]);

    const { dealerIds } = useMultipleDealerIds();

    const company = useMemo(() => {
        if (event.module.__typename === 'EventApplicationModule') {
            return event.module.company;
        }

        throw new Error('Invalid module');
    }, [event]);

    const downloadHandler = useCallback(
        async ({ format }: ExportModalInput) => {
            const getExportInfoByTab = (tab: Tab) => {
                switch (tab) {
                    case Tab.Reservations:
                        return { stage: ApplicationStage.Reservation, applicationIds: selectedReservations };

                    case Tab.Leads:
                        return { stage: ApplicationStage.Lead, applicationIds: selectedLeads };

                    case Tab.Appointments:
                        return { stage: ApplicationStage.Appointment, applicationIds: selectedAppointments };

                    default:
                        throw new Error('Invalid tab for downloading applications');
                }
            };

            const { stage, applicationIds } = getExportInfoByTab(currentTab);

            const password = await exportEventsApplication({
                eventId,
                applicationIds,
                stage,
                dealerIds,
                company,
                token,
                identifier,
                format,
            });

            exportFormatModal.close();

            if (password) {
                passwordModal.open(password);
            }
        },
        [
            currentTab,
            eventId,
            dealerIds,
            company,
            token,
            identifier,
            exportFormatModal,
            selectedReservations,
            selectedLeads,
            selectedAppointments,
            passwordModal,
        ]
    );

    const onDownloadApplicationClick = useCallback(() => {
        exportFormatModal.open();
    }, [exportFormatModal]);

    const extra = useMemo(() => {
        switch (currentTab) {
            case Tab.Reservations:
                return loadedReservationIds.length ? (
                    <Button
                        disabled={!selectedReservations.length}
                        icon={<DownloadOutlined />}
                        onClick={onDownloadApplicationClick}
                        type="primary"
                    >
                        {t('eventDetails:actions.download')}
                    </Button>
                ) : null;

            case Tab.Leads:
                return loadedLeadIds.length ? (
                    <Button
                        disabled={!selectedLeads.length}
                        icon={<DownloadOutlined />}
                        onClick={onDownloadApplicationClick}
                        type="primary"
                    >
                        {t('eventDetails:actions.download')}
                    </Button>
                ) : null;

            case Tab.Appointments:
                return loadedAppointmentIds.length ? (
                    <Button
                        disabled={!selectedAppointments.length}
                        icon={<DownloadOutlined />}
                        onClick={onDownloadApplicationClick}
                        type="primary"
                    >
                        {t('eventDetails:actions.download')}
                    </Button>
                ) : null;

            default:
                return null;
        }
    }, [
        Button,
        currentTab,
        loadedAppointmentIds.length,
        loadedLeadIds.length,
        loadedReservationIds.length,
        onDownloadApplicationClick,
        selectedAppointments.length,
        selectedLeads.length,
        selectedReservations.length,
        t,
    ]);

    const validate = useEventFormValidator();
    const onSubmit = useEventUpdateSubmission(event, goBack);
    const initialValues = useEventInitialValues(event);

    return (
        <GenericStateOnPageContextProvider refineContent={refineContentParams}>
            {passwordModal.render()}
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form id="eventForm" name="eventForm" onSubmitCapture={handleSubmit}>
                        <StyledBasicProPageWithHeader
                            extra={extra}
                            onBack={goBack}
                            style={{ background: '#fff', height: '100%' }}
                            title={event.displayName}
                        >
                            <EventDetailsTabs
                                activeTab={currentTab}
                                event={event}
                                pageType="Portal"
                                setActiveTab={setCurrentTab}
                            />
                            <EventDetailTabPanes
                                activeTab={currentTab}
                                disabled={disabled}
                                event={event}
                                goBack={goBack}
                                pageType="Portal"
                                setLoadedAppointmentIds={setLoadedAppointmentIds}
                                setLoadedLeadIds={setLoadedLeadIds}
                                setLoadedReservationIds={setLoadedReservationIds}
                                setSelectedAppointments={setSelectedAppointments}
                                setSelectedLeads={setSelectedLeads}
                                setSelectedReservations={setSelectedReservations}
                            />
                        </StyledBasicProPageWithHeader>
                    </Form>
                )}
            </Formik>

            {exportFormatModal.render({
                title: t('eventDetails:modals.downloadApplication.title'),
                description: t('eventDetails:modals.downloadApplication.description'),
                onDownload: downloadHandler,
            })}
        </GenericStateOnPageContextProvider>
    );
};

export default EventDetailsInnerPage;
